/**
 * SafetyFlagsView — admin review queue for caregiver safety pathway events.
 * Two separate paths by design (attorney review pending on reporting duties):
 *   "caregiver"      = risk of self-harm
 *   "care_recipient" = risk of harm to the person cared for
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";

const PATH_LABEL = {
    caregiver: { text: "Risk to caregiver (self-harm)", cls: "bg-red-50 text-red-700 border-red-200" },
    care_recipient: { text: "Risk to care recipient", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};
const CHOICE_LABEL = {
    yes_need_talk: "Said: safe, needs someone to talk to",
    not_sure: "Said: not sure if safe",
    not_safe: "Said: NOT safe",
};

export default function SafetyFlagsView() {
    const [flags, setFlags] = useState(null);

    const load = () => api.get("/admin/safety-flags")
        .then((r) => setFlags(r.data?.flags || []))
        .catch(() => toast.error("Couldn't load safety flags."));
    useEffect(() => { load(); }, []);

    const markReviewed = async (id) => {
        try {
            await api.put(`/admin/safety-flags/${id}/reviewed`);
            setFlags((f) => f.map((x) => (x.id === id ? { ...x, reviewed: true } : x)));
        } catch { toast.error("Couldn't update."); }
    };

    if (!flags) {
        return <section className="ml-card p-6 flex items-center gap-2 text-[#6E7A74]"><Loader2 className="animate-spin" size={18} /> Loading safety flags…</section>;
    }

    return (
        <div className="space-y-4" data-testid="safety-flags-view">
            <header className="ml-card p-5 flex items-center gap-2">
                <span className="rounded-full bg-red-50 text-red-600 p-1.5"><ShieldAlert size={14} strokeWidth={2} /></span>
                <div>
                    <h2 className="font-heading text-lg font-semibold text-[#1F2624] leading-tight">Safety flags</h2>
                    <p className="text-xs text-[#6E7A74]">
                        Caregiver check-in answers that triggered the crisis pathway. The caregiver was shown 988,
                        the Alzheimer's Helpline, and 911 immediately. Review each and mark done.
                    </p>
                </div>
            </header>
            {flags.length === 0 && (
                <section className="ml-card p-6 text-sm text-[#4C5551]" data-testid="safety-flags-empty">No flags — nothing has triggered the safety pathway.</section>
            )}
            {flags.map((f) => (
                <section key={f.id} className={`ml-card p-5 ${f.reviewed ? "opacity-60" : ""}`} data-testid={`safety-flag-${f.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] font-semibold border rounded-2xl px-2.5 py-0.5 ${(PATH_LABEL[f.path] || PATH_LABEL.caregiver).cls}`}>
                            {(PATH_LABEL[f.path] || PATH_LABEL.caregiver).text}
                        </span>
                        <span className="text-xs text-[#6E7A74] tabular-nums">{(f.created_at || "").replace("T", " ").slice(0, 16)} UTC</span>
                        <span className="text-xs font-mono text-[#1F2624]">{f.email}</span>
                        {f.safety_choice && <span className="text-xs text-[#8A5A2B] font-medium">{CHOICE_LABEL[f.safety_choice] || f.safety_choice}</span>}
                    </div>
                    <p className="mt-2 text-sm text-[#1F2624] bg-cream-light/60 rounded-xl px-3 py-2">&ldquo;{f.answer_excerpt}&rdquo;</p>
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-[#6E7A74]">From check-in: {f.checkin_code}</span>
                        {!f.reviewed ? (
                            <button onClick={() => markReviewed(f.id)} data-testid={`safety-flag-review-${f.id}`}
                                className="text-xs font-semibold text-teal-dark inline-flex items-center gap-1 hover:underline">
                                <CheckCircle2 size={13} /> Mark reviewed
                            </button>
                        ) : <span className="text-xs text-[#6E7A74] inline-flex items-center gap-1"><CheckCircle2 size={13} /> Reviewed</span>}
                    </div>
                </section>
            ))}
        </div>
    );
}
